import { useFormContext } from 'react-hook-form';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Grid, Box, Button, Dialog, DialogTitle, DialogContent, Typography, IconButton, Autocomplete, TextField } from '@mui/material';
import { Field } from 'src/components/hook-form/fields';
import { Iconify } from 'src/components/iconify';
import { ContactPersonForm } from './contact-person-form';
import axios, { endpoints } from 'src/utils/axios';
import { useParams } from 'src/routes/hooks';

export interface ContactPeopleSelectionProps { label: string; helperText?: string; renderStyle?: 'chip' | 'basic'; siteId?: number; gridProps?: any; }
export interface IContactPerson { id?: number; name: string; email: string; phoneNumber?: string; }

export function ContactPeopleSelection({ label, helperText, renderStyle = 'chip', siteId, gridProps }: ContactPeopleSelectionProps) {
  const { t } = useTranslation('site');
  const { watch, setValue } = useFormContext();
  const { id: customerId } = useParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<IContactPerson | null>(null);
  const [availableContacts, setAvailableContacts] = useState<IContactPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const contactPeople: IContactPerson[] = watch('contactPeople') || [];
  
  // Fetch available contact people for the customer
  useEffect(() => {
    const fetchContactPeople = async () => {
      if (!customerId) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${endpoints.contactPeople.list}?customerId=${customerId}`);
        // Handle paginated response or direct array
        const contacts = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || []);
        setAvailableContacts(contacts);
      } catch (error) {
        console.error('Failed to fetch contact people:', error);
        setAvailableContacts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContactPeople();
  }, [customerId]);
  const handleAddContact = useCallback(() => { setEditingContact(null); setDialogOpen(true); }, []);
  const handleEditContact = useCallback((contact: IContactPerson) => { setEditingContact(contact); setDialogOpen(true); }, []);
  const handleDeleteContact = useCallback((index: number) => { const updatedContacts = contactPeople.filter((_, i) => i !== index); setValue('contactPeople', updatedContacts, { shouldValidate: true, shouldDirty: true }); }, [contactPeople, setValue]);
  const handleSaveContact = useCallback((contactData: IContactPerson) => { if (editingContact) { const updatedContacts = contactPeople.map(c => c.email === editingContact.email ? contactData : c); setValue('contactPeople', updatedContacts, { shouldValidate: true, shouldDirty: true }); } else { setValue('contactPeople', [...contactPeople, contactData], { shouldValidate: true, shouldDirty: true }); } setDialogOpen(false); setEditingContact(null); }, [contactPeople, editingContact, setValue]);
  
  const handleSelectExisting = useCallback((event: any, value: IContactPerson[]) => {
    setValue('contactPeople', value, { shouldValidate: true, shouldDirty: true });
  }, [setValue]);
  
  const ContactPeopleField = (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      
      <Autocomplete
        multiple
        options={Array.isArray(availableContacts) ? availableContacts : []}
        value={Array.isArray(contactPeople) ? contactPeople : []}
        onChange={handleSelectExisting}
        getOptionLabel={(option) => `${option.name} (${option.email})`}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              availableContacts.length > 0
                ? "Select from existing contact people or create new"
                : "No existing contacts - click Add button to create"
            }
            size="small"
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id || index}
              label={`${option.name} (${option.email})`}
              size="small"
              color="info"
              variant="soft"
            />
          ))
        }
        isOptionEqualToValue={(option, value) =>
          option.id === value.id || option.email === value.email
        }
        loading={loading}
        disabled={availableContacts.length === 0}
        noOptionsText="No contact people available. Click Add button to create one."
        sx={{ mb: 2 }}
      />
      
      <Button
        variant="outlined"
        startIcon={<Iconify icon="solar:user-plus-bold" />}
        onClick={handleAddContact}
        size="small"
      >
        Add Contact Persona
      </Button>
      
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
  if (gridProps) { return (<><Grid item {...gridProps}>{ContactPeopleField}</Grid><ContactPersonDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleSaveContact} editingContact={editingContact} /></>); }
  return (<><>{ContactPeopleField}</><ContactPersonDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleSaveContact} editingContact={editingContact} /></>);
}

interface ContactPersonDialogProps { open: boolean; onClose: () => void; onSave: (contact: IContactPerson) => void; editingContact: IContactPerson | null; }
function ContactPersonDialog({ open, onClose, onSave, editingContact }: ContactPersonDialogProps) {
  const { t } = useTranslation('site');
  
  const handleSave = (contactData: IContactPerson) => {
    onSave(contactData);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={false}
      PaperProps={{
        sx: { width: '100%', maxWidth: 550 }
      }}
      onClick={(e) => e.stopPropagation()} // Prevent event bubbling
    >
      <DialogTitle>
        {editingContact
          ? t('form.contactPeople.modal.title.edit')
          : t('form.contactPeople.modal.title.add')}
      </DialogTitle>
      <DialogContent sx={{ pb: 7 }}>
        <ContactPersonForm
          initialData={editingContact}
          onSave={handleSave}
          onCancel={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}